import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { TrendingDown, Users, Layers, BarChart3, RefreshCw, AlertTriangle, Download, Mail } from "lucide-react";

function RetentionCell({ rate }: { rate: number }) {
  const color =
    rate >= 70 ? "bg-success/10 text-success" :
    rate >= 40 ? "bg-warning/10 text-warning" :
    "bg-destructive/10 text-destructive";
  return (
    <span className={`inline-flex items-center justify-center w-14 h-7 rounded text-xs font-semibold ${color}`}>
      {rate}%
    </span>
  );
}

export default function CohortsAnalysis() {
  const { data: cohorts, isLoading: cohLoading, refetch } = trpc.admin.analytics.cohorts.useQuery({ months: 12 });
  const { data: churnRisk, isLoading: churnLoading } = trpc.admin.analytics.churnRisk.useQuery();
  const { data: adoption, isLoading: adoptionLoading } = trpc.admin.analytics.featureAdoption.useQuery();

  const exportChurn = trpc.admin.analytics.exportChurnRisk.useMutation({
    onSuccess: (data: any) => {
      const blob = new Blob([data.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
    },
  });

  return (
    <AdminLayout>
      <div className="space-y-8 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Cohortes & Analyse Churn</h1>
            <p className="text-muted-foreground">Rétention mensuelle, risque de churn et adoption des features</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportChurn.mutate({} as any)}
              disabled={exportChurn.isPending}
            >
              <Download className="h-4 w-4 mr-2" />
              {exportChurn.isPending ? "Export…" : "Export Churn CSV"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </div>

        {/* Cohort Table */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Rétention par cohorte (12 derniers mois)
          </h2>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              {cohLoading ? (
                <div className="p-6 space-y-2">
                  {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : !cohorts || cohorts.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">Aucune donnée de cohorte disponible</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-4 py-3 font-medium">Cohorte</th>
                      <th className="text-right px-4 py-3 font-medium">Inscrits</th>
                      <th className="text-center px-4 py-3 font-medium">M1 (30j)</th>
                      <th className="text-center px-4 py-3 font-medium">M3 (60j)</th>
                      <th className="text-center px-4 py-3 font-medium">M6 (90j)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {cohorts.map((row) => (
                      <tr key={row.month} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3 font-medium">{row.month}</td>
                        <td className="px-4 py-3 text-right">{row.total.toLocaleString("fr-FR")}</td>
                        <td className="px-4 py-3 text-center"><RetentionCell rate={row.rate30} /></td>
                        <td className="px-4 py-3 text-center"><RetentionCell rate={row.rate60} /></td>
                        <td className="px-4 py-3 text-center"><RetentionCell rate={row.rate90} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Churn Risk */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <TrendingDown className="h-4 w-4" />
            Tenants à risque de churn
            {(churnRisk?.length ?? 0) > 0 && (
              <Badge variant="destructive">{churnRisk?.length}</Badge>
            )}
          </h2>
          <Card>
            <CardContent className="p-0">
              {churnLoading ? (
                <div className="p-6 space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : !churnRisk || churnRisk.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  Aucun tenant à risque identifié
                </div>
              ) : (
                <div className="divide-y">
                  {churnRisk.map((t) => {
                    const days = t.daysSinceLogin;
                    const risk =
                      days === null ? "high" :
                      days >= 60 ? "high" :
                      days >= 30 ? "medium" : "low";
                    return (
                      <div key={t.id} className="flex items-center justify-between px-4 py-3 text-sm">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className={`h-4 w-4 ${risk === "high" ? "text-destructive" : risk === "medium" ? "text-warning" : "text-muted-foreground"}`} />
                          <div>
                            <div className="font-medium">{t.name}</div>
                            <div className="text-xs text-muted-foreground">
                              Dernière connexion : {days !== null ? `il y a ${days}j` : "jamais"}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>{t.totalQuotes} devis</span>
                          <span>{t.totalInvoices} factures</span>
                          <Badge variant={t.plan ? "secondary" : "outline"}>{t.plan ?? "no plan"}</Badge>
                          {t.email && (
                            <a
                              href={`mailto:${t.email}?subject=Votre%20compte%20Constrigo&body=Bonjour%2C%0A%0ANous%20avons%20remarqué%20que%20vous%20n%27avez%20pas%20utilisé%20Constrigo%20récemment.%20Pouvons-nous%20vous%20aider%20%3F`}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-xs font-medium"
                            >
                              <Mail className="h-3 w-3" />
                              Contacter
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Feature Adoption */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Adoption des modules
            {adoption && (
              <span className="text-xs text-muted-foreground font-normal">
                — sur {adoption.totalActive} tenants actifs
              </span>
            )}
          </h2>
          <Card>
            <CardContent className="p-6 space-y-4">
              {adoptionLoading ? (
                Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)
              ) : !adoption?.modules.length ? (
                <div className="text-center text-muted-foreground">Aucune donnée d'adoption</div>
              ) : (
                adoption.modules
                  .sort((a, b) => b.adoptionRate - a.adoptionRate)
                  .map((mod) => (
                    <div key={mod.name} className="flex items-center gap-4">
                      <div className="w-28 text-sm font-medium capitalize">{mod.name}</div>
                      <div className="flex-1 bg-muted rounded-full h-3 overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all"
                          style={{ width: `${mod.adoptionRate}%` }}
                        />
                      </div>
                      <div className="w-20 text-sm text-right">
                        <span className="font-semibold">{mod.adoptionRate}%</span>
                        <span className="text-muted-foreground ml-1 text-xs">({mod.tenantsUsing})</span>
                      </div>
                    </div>
                  ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
